import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    Heading,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';

import { useSetAnalyticsScreen } from '../../navigation';
import { apiClient, useUpgrade } from '../../state';
import { DocumentationLink } from '../Link';
import {
    MergeRulesConfigurationEditor,
    useMergeRulesConfigurationEditor,
} from './MergeRulesConfigurationEditor';
import { MergeRulesUpgradeAlert } from './MergeRulesUpgradeAlert';

/**
 * Dialog to configure the merge rules.
 */
export function MergeRulesDialog(props: { space: api.Space }) {
    const { space } = props;

    return (
        <Dialog size={{ width: 640 }}>
            <DialogHeader>
                <Heading>Configure merge rule for this space</Heading>
                <Description>
                    Define the rules for merging change requests into this space.{' '}
                    <DocumentationLink path="collaboration/merge-rules">
                        Learn more
                    </DocumentationLink>
                    .
                </Description>
            </DialogHeader>
            <SpaceMergeRulesDialogContent space={space} />
        </Dialog>
    );
}

function SpaceMergeRulesDialogContent(props: { space: api.Space }) {
    const { space } = props;

    const upgrade = useUpgrade('change-requests-merge-rules');
    const [isPending, withDialogAction] = useDialogAction();
    const configEditor = useMergeRulesConfigurationEditor(space.mergeRules);

    useSetAnalyticsScreen(
        analytics.createSpaceEvent(space, {
            screen: 'space_merge_rules',
        }),
        [space.id]
    );

    const onSubmit = async () => {
        await withDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                await apiClient.spaces.updateSpaceById(space.id, {
                    mergeRules: configEditor.configuration,
                });
                toast.success('Merge rules updated');
                await close();
            }, 'Error while updating merge rules');
        });
    };

    return (
        <>
            <DialogBody className="flex flex-col gap-6">
                {upgrade ? <MergeRulesUpgradeAlert upgrade={upgrade} /> : null}
                <MergeRulesConfigurationEditor
                    configuration={configEditor.configuration}
                    onChange={configEditor.onChange}
                    allowInherit
                />
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Button
                    label="Save"
                    onPress={onSubmit}
                    disabled={!configEditor.hasChanges || isPending}
                    {...(configEditor.hasChanges ? upgrade : undefined)}
                />
            </DialogFooter>
        </>
    );
}
